import type { ShowcaseContentItemDto } from '@repo/api/web/models'
import { withoutTrailingSlash } from 'ufo'
import type { Ref } from 'vue'

import { breadcrumbJsonLd, jsonLdScript, newsArticleJsonLd } from '../utils/jsonLd'

/**
 * SEO-мета страницы контента (новость/статья/статическая страница, CMS MR 7):
 * гибридная модель — админская мета имеет приоритет, пусто = авто-генерация
 * (title ← заголовок, description ← анонс). Canonical-override и noindex —
 * из SEO-блока материала; og:type=article и JSON-LD NewsArticle — для новостей.
 * Title — bare (суффикс сайта добавляет titleTemplate seoRoot-плагина)
 */
export const useContentSeo = (
  item: Ref<ShowcaseContentItemDto | null | undefined>,
  options?: {
    article?: boolean
    /** Раздел-родитель для хлебных крошек («Новости» → `/news`). */
    breadcrumb?: { name: string, path: string }
    /**
     * Заголовок страницы, когда материала в CMS нет: страницы модуля `Info`
     * показывают встроенный текст шаблона и не должны терять мету (MR 12a)
     */
    fallbackTitle?: string
    /** Описание для того же случая. */
    fallbackDescription?: string
  },
) => {
  const { service: seo } = useSeo()
  const url = useRequestURL()
  const siteName = useSiteName()

  const pageTitle = () =>
    item.value?.seo?.metaTitle || item.value?.title || options?.fallbackTitle
  const pageDescription = () =>
    item.value?.seo?.metaDescription
    || item.value?.announce
    || item.value?.title
    || options?.fallbackDescription
    || options?.fallbackTitle

  // og-картинку и JSON-LD считаем жадно в setup-контексте (см. useProductDetailsSeo)
  const ogImage = ref<string | undefined>()
  watchEffect(() => {
    ogImage.value
      = seo.ogImageById(item.value?.seo?.ogImageId ?? item.value?.coverId)
        ?? seo.defaultOgImage()
  })

  const jsonLdScripts = ref<ReturnType<typeof jsonLdScript>[]>([])
  watchEffect(() => {
    if (!item.value) {
      jsonLdScripts.value = []
      return
    }

    const scripts = []

    if (options?.article) {
      scripts.push(jsonLdScript(newsArticleJsonLd({
        headline: item.value.title,
        url: withoutTrailingSlash(`${url.origin}${url.pathname}`),
        description: item.value.seo?.metaDescription
          || item.value.announce
          || undefined,
        image: ogImage.value,
        datePublished: item.value.publishAt ?? undefined,
        siteName: siteName.value,
      })))
    }

    // цепочка «Главная → раздел → материал» (раздел задаёт страница)
    scripts.push(jsonLdScript(breadcrumbJsonLd([
      { name: 'Главная', url: `${url.origin}/` },
      ...(options?.breadcrumb
        ? [{
            name: options.breadcrumb.name,
            url: `${url.origin}${options.breadcrumb.path}`,
          }]
        : []),
      { name: item.value.title },
    ])))

    jsonLdScripts.value = scripts
  })

  useSeoMeta({
    title: pageTitle,
    description: pageDescription,

    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogImage: () => ogImage.value,

    // noindex материала (seo.noIndex); при false — не задаём, действует
    // глобальное правило seoRoot по префиксам путей
    robots: () => (item.value?.seo?.noIndex ? 'noindex, follow' : undefined),
  })

  useHead(computed(() => ({
    script: jsonLdScripts.value,
    meta: options?.article
      ? [{ property: 'og:type', content: 'article' }]
      : [],
    // canonical-override из админки перекрывает canonical seoRoot по key
    link: item.value?.seo?.canonicalUrl
      ? [{ key: 'canonical', rel: 'canonical', href: item.value.seo.canonicalUrl }]
      : [],
  })))
}
